import { Link } from "react-router-dom";
import { Package, Dna, Droplets, Radio, ShieldAlert, Snowflake, GraduationCap, Youtube, Play, FileText, Image as ImageIcon, Download } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { useVideo } from "@/components/VideoModal";
import { CargoDonut, BiogenesisFlow } from "@/components/ExosomeDiagrams";
import { COMPLIANCE } from "@/data/site";

const CARGO = [
  { icon: Package, t: "Proteins", d: "Growth factors, cytokines and transmembrane proteins involved in cell signalling and immune communication." },
  { icon: Dna, t: "Nucleic acids", d: "mRNA and miRNA molecules that can influence gene expression in recipient cells — the basis of exosome research in regeneration." },
  { icon: Droplets, t: "Lipids", d: "A protective phospholipid bilayer that shields the cargo and helps it fuse with target cell membranes." },
];

const VideoTile = ({ id, title, tag }) => {
  const { openVideo } = useVideo();
  return (
    <Reveal>
      <button
        onClick={() => openVideo(id)}
        className="group flex h-full w-full flex-col border border-[#e4e9ec] bg-white text-left shadow-sm transition-shadow hover:shadow-md"
        data-testid={`video-tile-${id}`}
        aria-label={`Play video: ${title}`}
      >
        <span className="relative block aspect-video w-full overflow-hidden bg-[#0a1112]">
          <img
            src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
            alt={title}
            className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
            loading="lazy"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/95 text-[#0a0a0a] shadow-lg transition-transform group-hover:scale-110">
              <Play className="ml-0.5 h-5 w-5" fill="currentColor" />
            </span>
          </span>
        </span>
        <span className="flex flex-1 flex-col gap-1 p-5">
          <span className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">{tag}</span>
          <span className="text-sm font-medium leading-snug text-[#0a0a0a]">{title}</span>
        </span>
      </button>
    </Reveal>
  );
};

const MECHANISMS = [
  { n: "01", t: "Recruitment & signalling", d: "In research settings, exosomes are observed signalling to the body's own stem cells and drawing them toward areas of injury — kickstarting natural repair pathways." },
  { n: "02", t: "Growth-factor delivery", d: "They release bioactive molecules such as VEGF and TGF-β, studied for their roles in cell growth, blood-vessel formation and tissue remodelling." },
  { n: "03", t: "Gene regulation", d: "By transferring miRNA and mRNA, exosomes can dial gene expression up or down — a mechanism researchers link to reduced inflammation and enhanced regeneration." },
];

const RESEARCH_AREAS = [
  { t: "Hair-follicle research", d: "Studies explore how MSC-derived exosomes stimulate dermal papilla cells and follicle stem cells, prolong the growth (anagen) phase and support hair density through miRNA activity." },
  { t: "Skin & collagen research", d: "Exosome research examines collagen synthesis, extracellular-matrix remodelling and fibroblast activation — pathways behind elasticity, fine lines and firmness." },
  { t: "Barrier & soothing research", d: "Anti-inflammatory miRNAs such as miR-146a are studied for calming redness and supporting barrier repair." },
  { t: "Wider regenerative research", d: "Beyond aesthetics, published work investigates tissue repair, joint and cartilage support, and neurological applications — contexts that require approved therapeutic products." },
];

const HANDLING = [
  { icon: Snowflake, t: "Cold-chain storage", d: "Professional freeze-dried and frozen exosome formats are typically stored at defined low temperatures; each batch carries a validated expiry date set at processing." },
  { icon: ShieldAlert, t: "Single-use discipline", d: "Professional vials are single-use: once thawed and opened, contents should be used immediately and never re-frozen." },
  { icon: GraduationCap, t: "Trained application", d: "Professional formats are intended for trained operators following the manufacturer's preparation protocol — never for home modification of products." },
];

export default function Education() {
  return (
    <div className="pt-16" data-testid="education-page">
      <section className="lab-grid border-b border-[#e4e9ec]">
        <div className="mx-auto max-w-[1440px] px-5 pb-16 pt-20 md:px-10 md:pb-20 md:pt-24">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Learn · Exosome research &amp; science</p>
            <h1 className="font-display mt-4 text-[clamp(2.4rem,5.5vw,4.6rem)] leading-[0.98] tracking-tight" data-testid="education-headline">
              Research <em>education</em>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#56636b]" data-testid="education-intro">
              A plain-language primer on extracellular vesicles — what they are, what they carry, how professional
              channel technologies are studied alongside them, and why cosmetic science is so interested. Educational
              content only; see our disclaimer at the end.
            </p>
          </Reveal>
        </div>
      </section>

      {/* BRAND EDUCATION BOARD */}
      <section className="border-t border-[#e4e9ec] py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Brand education board</p>
            <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">The next evolution of professional bio-aesthetics</h2>
            <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[#56636b]">
              Professional education in practice — EX+ International Trainer Certification holders and the four
              Vantis product families with the cosmetic benefits each is formulated to support.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 border border-[#e4e9ec] bg-white p-3 shadow-sm sm:p-5" data-testid="education-board-card">
              <a
                href="/vantis-professional-bio-aesthetics.jpg"
                target="_blank"
                rel="noreferrer"
                data-testid="education-board-link"
                className="block"
                aria-label="Open the professional bio-aesthetics education board at full size"
              >
                <img
                  src="/vantis-professional-bio-aesthetics.jpg"
                  alt="The next evolution of professional bio-aesthetics — EX+ certified international trainers Nikki Anand, Leil ani Kopp and Amber Dirkson, with the Vantis Facial Spray, Vantis Hair Conditioning Fluid, Vantis Beauty Cosmetic Exosomes Powder and Vantis Aestheti-Mist Technology"
                  className="mx-auto w-full object-contain"
                  loading="lazy"
                />
              </a>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#e4e9ec] pt-4">
                <p className="font-lab text-[10px] uppercase tracking-[0.18em] text-[#56636b]">
                  Skincare · Aesthetics · Cosmetic formulators · Medi-spa professionals
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="/vantis-professional-bio-aesthetics.jpg"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="education-board-open"
                    className="inline-flex items-center gap-2 border border-[#0a0a0a] px-5 py-2.5 text-sm transition-colors hover:bg-[#0a0a0a] hover:text-white"
                  >
                    Open full size
                  </a>
                  <a
                    href="/vantis-professional-bio-aesthetics.jpg"
                    download="vantis-professional-bio-aesthetics.jpg"
                    data-testid="education-board-download"
                    className="inline-flex items-center gap-2 bg-[#0a0a0a] px-5 py-2.5 text-sm text-white transition-colors hover:bg-[#0e8c8c]"
                  >
                    Download board
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-lab mt-6 text-[10px] leading-relaxed text-[#8a959c]" data-testid="education-board-disclaimer">
              Vantis products are cosmetic products and are not intended to diagnose, treat, cure, or prevent any
              disease or medical condition. Results may vary.
            </p>
          </Reveal>
        </div>
      </section>

      {/* AMSC 2026 CONFERENCE TALKS */}
      <section className="border-t border-[#e4e9ec] py-16 md:py-20" data-testid="education-videos-section">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">AMSC 2026 · Malaysia — Conference talks</p>
            <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">The doctors behind the science</h2>
            <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[#56636b]">
              Clinicians and researchers presenting the Exoplus Bio platform at the Aesthetic &amp; Medical Science
              Conference 2026 in Malaysia — watch the talks on demand.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              { id: "fX6EqIWfIUM", title: "Dr. Chris Tsai of Taiwan Research at AMSC 2026 Malaysia", tag: "Taiwan · Research" },
              { id: "FYbc618ILQc", title: "Dr. Pamela Ruri of Malaysia at AMSC 2026 Malaysia", tag: "Malaysia · Clinical" },
              { id: "n2qhsi_Pe0E", title: "Dr. Tim Hsu of Australia at AMSC 2026 Malaysia", tag: "Australia · Clinical" },
              { id: "DpT3dNhHB_4", title: "Dr. Ting Song Lim of Malaysia Research", tag: "Malaysia · Research" },
              { id: "RSDgR6OZv9g", title: "Jeffrey Chen of ExoPlus Bio Inc. at AMSC 2026 Malaysia", tag: "ExoPlus Bio Inc." },
            ].map((v) => (
              <VideoTile key={v.id} id={v.id} title={v.title} tag={v.tag} />
            ))}
          </div>
        </div>
      </section>

      {/* WHAT THEY ARE */}
      <section className="mx-auto max-w-[1100px] px-5 py-20 md:px-10 md:py-24">
        <Reveal>
          <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">01 — The basics</p>
          <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">Nature's microscopic messengers</h2>
          <p className="mt-6 text-[15px] leading-relaxed text-[#0a0a0a]">
            Exosomes are tiny, cell-derived vesicles — commonly described in the 30–150 nanometre range — that cells
            release to talk to one another. Enclosed in a lipid membrane, they ferry proteins, genetic material and
            other signalling molecules between cells. Because they are <em>acellular</em> (they contain no living
            cells and cannot replicate), researchers study them as a way to capture the signalling benefits associated
            with cell-therapy research while avoiding risks tied to live cells, such as immune rejection.
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-[#56636b]">
            Exosomes can be harvested from different cell sources — mesenchymal stem cells (MSCs), plant cells, egg
            white and even milk-derived sources. Published research suggests the source influences the vesicle cargo
            and, with it, the behaviour being studied. Every product in our collection names its source openly, from
            plant-derived to UC-MSC to milk exosomes.
          </p>
        </Reveal>
      </section>

      {/* CARGO */}
      <section className="border-y border-[#e4e9ec] py-20 md:py-24">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">02 — The cargo</p>
            <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">What's inside a vesicle</h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#56636b]">
              Exosomes carry a targeted cargo inherited from their source cell:
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 lg:col-span-7 lg:content-start">
              {CARGO.map((c, i) => (
                <Reveal key={c.t} delay={i * 0.07}>
                  <div className="h-full border border-[#e4e9ec] bg-white p-6" data-testid={`education-cargo-${i}`}>
                    <c.icon className="h-5 w-5 text-[#0e8c8c]" />
                    <h3 className="font-display mt-4 text-lg">{c.t}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="border border-[#e4e9ec] bg-white p-6" data-testid="education-donut-card">
                <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#56636b]">
                  Fig. 1 — Typical exosome cargo composition
                </p>
                <div className="mt-4">
                  <CargoDonut />
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 border-l-2 border-[#0e8c8c] bg-[#e3f2f0]/40 p-5 text-sm leading-relaxed text-[#56636b]">
              A well-prepared exosome preparation is judged on three practical qualities: <strong className="text-[#0a0a0a]">purity</strong>{" "}
              (sterile processing and filtration), <strong className="text-[#0a0a0a]">potency</strong> (a stated particle count
              per millilitre, verified by analysis) and <strong className="text-[#0a0a0a]">performance</strong> (consistent
              quality from batch to batch, documented by a Certificate of Analysis).
            </p>
          </Reveal>
        </div>
      </section>

      {/* MECHANISMS */}
      <section className="mx-auto max-w-[1100px] px-5 py-20 md:px-10 md:py-24">
        <Reveal>
          <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">03 — Mechanisms</p>
          <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">How exosomes work (in research)</h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#56636b]">
            Scientific literature describes a three-part signalling behaviour:
          </p>
        </Reveal>
        <div className="mt-10 space-y-0 border-t border-[#e4e9ec]">
          {MECHANISMS.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.06}>
              <div className="flex gap-6 border-b border-[#e4e9ec] py-6" data-testid={`education-mechanism-${i}`}>
                <span className="font-lab text-[10px] tracking-[0.2em] text-[#0e8c8c]">{m.n}</span>
                <div>
                  <h3 className="font-display text-xl">{m.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#56636b]">{m.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-10 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-biogenesis-card">
            <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#56636b]">
              Fig. 2 — Exosome formation: biogenesis & release
            </p>
            <div className="mt-6 overflow-x-auto" data-testid="diagram-biogenesis-wrap">
              <div className="min-w-[820px]">
                <BiogenesisFlow />
              </div>
            </div>
            <p className="font-lab mt-4 text-[10px] leading-relaxed text-[#56636b]">
              Mesenchymal stem cells naturally produce exosomes; specialised processes isolate and purify them while
              protecting their 3-D structure and bioactivity from enzymatic breakdown, oxidation and freeze damage.
            </p>
          </div>
        </Reveal>
      </section>

      {/* RESEARCH AREAS */}
      <section className="border-y border-[#e4e9ec] py-20 md:py-24">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">04 — Research directions</p>
            <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">Where the science is heading</h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#56636b]">
              These are published research directions — descriptions of scientific study, not claims about our
              cosmetic products:
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {RESEARCH_AREAS.map((r, i) => (
              <Reveal key={r.t} delay={(i % 2) * 0.07}>
                <div className="h-full border border-[#e4e9ec] bg-white p-7" data-testid={`education-research-${i}`}>
                  <h3 className="font-display text-xl">{r.t}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">{r.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HANDLING */}
      <section className="mx-auto max-w-[1100px] px-5 py-20 md:px-10 md:py-24">
        <Reveal>
          <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">05 — Handling & storage</p>
          <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">Keeping formats fresh</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {HANDLING.map((h, i) => (
            <Reveal key={h.t} delay={i * 0.07}>
              <div className="h-full border border-[#e4e9ec] bg-white p-7" data-testid={`education-handling-${i}`}>
                <h.icon className="h-5 w-5 text-[#0e8c8c]" />
                <h3 className="font-display mt-4 text-lg">{h.t}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">{h.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SPOTLIGHT: MILK EXOSOMES */}
      <section className="border-t border-[#e4e9ec] py-20 md:py-24">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">06 — Ingredient spotlight</p>
            <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">Milk-derived exosomes</h2>
            <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[#0a0a0a]">
              Milk exosomes are nano-sized extracellular vesicles — commonly described in the 50–200 nanometre range —
              surrounded by the same kind of lipid bilayer as other exosomes and carrying proteins, lipids and nucleic
              acids. Cosmetic ingredient research has taken a keen interest in them: milk is an abundant, well-understood
              source, and its vesicles can be isolated gently and concentrated for skincare study. Our Evora Facial duo
              is built on milk-derived exosomes — 2.1 trillion per kit, applied with a precision airgun in professional
              settings.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              { icon: Droplets, t: "From whey", d: "Milk's protein is roughly 80% casein and 20% whey. Whey — the liquid left after milk curdles in cheese-making — is rich in essential amino acids, proteins, lactose, minerals and vitamins, and is studied for moisturizing and skin-regenerating properties." },
              { icon: GraduationCap, t: "Studied effects", d: "Milk-exosome ingredients are researched for antioxidant and anti-aging effects, with whey components linked in cosmetic literature to moisturization and the look of improved wrinkles — appearance claims within a cosmetic frame." },
              { icon: Package, t: "Measured in units", d: "Exosome content is stated in EV units: one unit equals ten million (10⁷) extracellular-vesicle nanoparticles per millilitre. Concentration labelling keeps potency transparent and comparable." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.07}>
                <div className="h-full border border-[#e4e9ec] bg-white p-6" data-testid={`education-milk-${i}`}>
                  <c.icon className="h-5 w-5 text-[#0e8c8c]" />
                  <h3 className="font-display mt-4 text-lg">{c.t}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-visualization-card">
              <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#56636b]">How vesicles are visualized &amp; verified</p>
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="border border-[#e4e9ec] p-5">
                  <h4 className="font-display text-lg">Cryo-TEM analysis</h4>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">
                    Cryo-transmission electron microscopy flash-freezes samples at ultra-low temperatures so vesicles
                    can be photographed essentially in their natural state, non-invasively. It is how a preparation's
                    vesicle structure is confirmed — scale bars as fine as 50&nbsp;nm.
                  </p>
                </div>
                <div className="border border-[#e4e9ec] p-5">
                  <h4 className="font-display text-lg">Oil-red O staining</h4>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">
                    Oil-red O dye binds the phospholipids in a vesicle's membrane barrier, staining them red under the
                    microscope. Seeing that membrane is how analysts confirm intact vesicles rather than loose
                    fragments — scale bars around 100&nbsp;nm.
                  </p>
                </div>
              </div>
              <p className="font-lab mt-6 text-[10px] leading-relaxed text-[#56636b]">
                Ingredient facts: INCI name "Milk Exosomes" · studied for antioxidant &amp; anti-aging effects ·
                intended for all skin-care cosmetics. Educational description only — appearance claims within a
                cosmetic frame.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EVIDENCE */}
      <section className="mx-auto max-w-[1100px] px-5 py-20 md:px-10 md:py-24">
        <Reveal>
          <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">07 — The evidence</p>
          <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">How milk exosomes are measured &amp; tested</h2>
          <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[#0a0a0a]">
            Ingredient manufacturers characterize milk-exosome preparations with standard laboratory methods before
            any cosmetic use. Here is what a typical research dossier reports — numbers shown are from the
            manufacturer's in-vitro study material, presented for education, not as claims about our cosmetics.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal delay={0.05}>
            <div className="h-full border border-[#e4e9ec] bg-white p-7" data-testid="education-nta-card">
              <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">Quantification — NTA</p>
              <h3 className="font-display mt-3 text-xl">Nanoparticle Tracking Analysis</h3>
              <p className="mt-3 text-[13px] leading-relaxed text-[#56636b]">
                An NTA instrument tracks the Brownian motion of individual vesicles in suspension; faster jiggling
                means smaller particles. From that diffusion it reports a concentration and a size-distribution curve —
                typically peaking around 100–200&nbsp;nm.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-4">
                <div className="border border-[#e4e9ec] p-4">
                  <p className="font-display text-2xl">1.2×10¹⁰</p>
                  <p className="font-lab mt-1 text-[9px] uppercase tracking-[0.14em] text-[#56636b]">particles / mL</p>
                </div>
                <div className="border border-[#e4e9ec] p-4">
                  <p className="font-display text-2xl">157.3 nm</p>
                  <p className="font-lab mt-1 text-[9px] uppercase tracking-[0.14em] text-[#56636b]">mean particle size</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="h-full border border-[#e4e9ec] bg-white p-7" data-testid="education-mirna-card">
              <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">Quantification — miRNA</p>
              <h3 className="font-display mt-3 text-xl">The major active, measured</h3>
              <p className="mt-3 text-[13px] leading-relaxed text-[#56636b]">
                Small RNA is isolated from the vesicles and quantified by UV absorbance — nucleic acids absorb
                strongly at 260&nbsp;nm, giving a clean confirmation peak. Cross-kingdom miRNA regulation is the
                mechanism researchers study for skin applications.
              </p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[["76.1", "ng/µL"], ["1.83", "A260/A280"], ["2.21", "A260/A230"]].map(([v, l]) => (
                  <div key={l} className="border border-[#e4e9ec] p-3 text-center">
                    <p className="font-display text-xl">{v}</p>
                    <p className="font-lab mt-1 text-[9px] uppercase tracking-[0.1em] text-[#56636b]">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-6 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-sod2-card">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
              <div className="md:col-span-7">
                <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">Efficacy verification — in-vitro assay</p>
                <h3 className="font-display mt-3 text-2xl">SOD-2: the antioxidant enzyme test</h3>
                <p className="mt-4 text-[13px] leading-relaxed text-[#56636b]">
                  Researchers applied milk extract and milk exosomes to human dermal fibroblasts (HDF) — the cells that
                  build your skin's collagen — at concentrations from 1×10⁶ to 1×10⁷ particles/mL, with vitamin C as
                  the positive control. The result: expression of <strong className="text-[#0a0a0a]">SOD-2</strong>{" "}
                  (superoxide dismutase 2, a natural antioxidant enzyme) increased across the treatment range.
                </p>
                <p className="mt-3 text-[13px] leading-relaxed text-[#56636b]">
                  The logic: SOD-2 helps neutralize reactive oxygen species — the free radicals behind oxidative
                  stress. An ingredient that supports SOD-2 expression is therefore studied as an antioxidant, and
                  antioxidant activity is the research pathway of interest for anti-aging cosmetics.
                </p>
                <p className="font-lab mt-5 border-l-2 border-[#b4690e] bg-[#b4690e]/5 p-4 text-[10px] leading-relaxed text-[#56636b]">
                  In-vitro research finding from the ingredient manufacturer's dossier — laboratory cell study, not a
                  clinical trial and not a claim about Exoplus Bio cosmetic products.
                </p>
              </div>
              <div className="md:col-span-5">
                <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#56636b]">SOD-2 expression (fold change) — illustrative trend</p>
                <svg viewBox="0 0 320 240" className="mt-4 w-full" role="img" aria-label="Illustrative bar chart of SOD-2 expression increasing across treatments" data-testid="diagram-sod2">
                  <line x1="20" y1="200" x2="310" y2="200" stroke="#e4e9ec" strokeWidth="1.5" />
                  {[
                    ["CTL", 55, "#9aa7ad"], ["5%", 80, "#9fd9d5"], ["10%", 100, "#9fd9d5"],
                    ["10⁶/mL", 125, "#0e8c8c"], ["10⁷/mL", 150, "#0e8c8c"], ["Vit C", 165, "#0a0a0a"],
                  ].map(([label, h, color], i) => (
                    <g key={label}>
                      <rect x={30 + i * 48} y={200 - h} width="30" height={h} fill={color} opacity="0.9" />
                      <text x={45 + i * 48} y="216" textAnchor="middle" fontSize="9" fill="#56636b">{label}</text>
                    </g>
                  ))}
                  <text x="160" y="236" textAnchor="middle" fontSize="9" fill="#56636b">treatment (fold change vs control — illustrative)</text>
                </svg>
              </div>
            </div>
          </div>
        </Reveal>

        {/* CAT ASSAY + SPEC SHEET */}
        <Reveal delay={0.05}>
          <div className="mt-6 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-cat-card">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
              <div className="md:col-span-7">
                <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">Efficacy verification — second enzyme test</p>
                <h3 className="font-display mt-3 text-2xl">CAT: catalase, the other half of the defense</h3>
                <p className="mt-4 text-[13px] leading-relaxed text-[#56636b]">
                  The same in-vitro design — human dermal fibroblasts, milk extract at 5% and 10%, milk exosomes at
                  1×10⁶ and 1×10⁷ particles/mL, vitamin C as the positive control — was run for{" "}
                  <strong className="text-[#0a0a0a]">CAT (catalase)</strong>. Result: CAT expression also increased in a
                  concentration-dependent way, with the exosome treatments showing the strongest response.
                </p>
                <p className="mt-3 text-[13px] leading-relaxed text-[#56636b]">
                  Together the two enzymes form the skin's classic two-step antioxidant defense: SOD-2 converts
                  superoxide radicals into hydrogen peroxide, and catalase breaks that peroxide down into water and
                  oxygen. An ingredient researched for supporting <em>both</em> steps is why milk exosomes appear in
                  anti-aging cosmetic literature.
                </p>
                <p className="font-lab mt-5 border-l-2 border-[#b4690e] bg-[#b4690e]/5 p-4 text-[10px] leading-relaxed text-[#56636b]">
                  In-vitro research finding from the ingredient manufacturer's dossier — laboratory cell study, not a
                  clinical trial and not a claim about Exoplus Bio cosmetic products.
                </p>
              </div>
              <div className="md:col-span-5">
                <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#56636b]">CAT expression (fold change) — illustrative trend</p>
                <svg viewBox="0 0 320 240" className="mt-4 w-full" role="img" aria-label="Illustrative bar chart of CAT expression increasing across treatments" data-testid="diagram-cat">
                  <line x1="20" y1="200" x2="310" y2="200" stroke="#e4e9ec" strokeWidth="1.5" />
                  {[
                    ["CTL", 60, "#9aa7ad"], ["5%", 85, "#9fd9d5"], ["10%", 105, "#9fd9d5"],
                    ["10⁶/mL", 130, "#0e8c8c"], ["10⁷/mL", 158, "#0e8c8c"], ["Vit C", 162, "#0a0a0a"],
                  ].map(([label, h, color], i) => (
                    <g key={label}>
                      <rect x={30 + i * 48} y={200 - h} width="30" height={h} fill={color} opacity="0.9" />
                      <text x={45 + i * 48} y="216" textAnchor="middle" fontSize="9" fill="#56636b">{label}</text>
                    </g>
                  ))}
                  <text x="160" y="236" textAnchor="middle" fontSize="9" fill="#56636b">treatment (fold change vs control — illustrative)</text>
                </svg>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-6 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-spec-card">
            <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">Ingredient specification sheet</p>
            <h3 className="font-display mt-3 text-2xl">What a professional spec looks like</h3>
            <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-[#56636b]">
              Every cosmetic ingredient ships with a specification. For the milk-exosome ingredient, the manufacturer's
              sheet reads (INCI: Water (and) Milk Exosomes (and) Glycerin (and) 1,2-Hexanediol):
            </p>
            <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4" data-testid="education-spec-grid">
              {[
                ["Appearance", "Colorless to light-yellow liquid"],
                ["Odor", "Characteristic"],
                ["pH", "6.00 – 8.00"],
                ["Refractive index", "1.335 – 1.355 (n20/D)"],
                ["Identification", "≥ 1.0×10⁷ particles/mL (NTA)"],
                ["Heavy metals", "< 20 ppm"],
                ["Arsenic", "< 2 ppm"],
                ["Microbial count", "< 100 CFU/g"],
                ["Storage", "Below 15 °C, cool, dry, dark"],
                ["Shelf life", "24 months from manufacture"],
                ["Incompatible with", "Oxidizers, acids, bases"],
                ["Recommended use", "1 – 5% in formulation"],
              ].map(([k, v]) => (
                <div key={k} className="border border-[#e4e9ec] p-3.5">
                  <p className="font-lab text-[9px] uppercase tracking-[0.12em] text-[#56636b]">{k}</p>
                  <p className="mt-1 text-[12px] leading-snug text-[#0a0a0a]">{v}</p>
                </div>
              ))}
            </div>
            <p className="font-lab mt-6 text-[10px] leading-relaxed text-[#56636b]">
              Why it matters: those heavy-metal, microbial and identification limits are the same discipline behind the
              Certificate of Analysis shown on our Compliance page — quality control happens at the ingredient level,
              batch by batch.
            </p>
          </div>
        </Reveal>
      </section>

      {/* RESEARCH CHAPTER — ENERGY-BASED CHANNELS x BIOMATERIALS */}
      <section className="border-t border-[#e4e9ec] py-20 md:py-24">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Research chapter · Professional channel technologies</p>
            <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">When channels meet biomaterials</h2>
            <p className="mt-5 max-w-3xl text-[15px] leading-relaxed text-[#56636b]">
              Professional regenerative research increasingly pairs energy-based devices — fractional lasers and
              micro-needling — with topical biomaterials such as PRP, exosomes, PDRN and scaffolds. The device creates
              temporary microscopic channels through the skin barrier; the biomaterials then reach the living dermis.
              Studied together, the combination outperformed either approach alone in published clinic work. Here is the
              mechanism, in plain language.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-8 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-channels-card">
              <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">The delivery problem — and the channel solution</p>
              <h3 className="font-display mt-3 text-2xl">A barrier built to keep things out</h3>
              <p className="mt-4 text-[13px] leading-relaxed text-[#56636b]">
                The stratum corneum — the skin's outermost layer — is a lipid-protein wall that blocks most of what
                touches it. Only small, oil-soluble molecules (under ~500 Daltons) cross it passively; large biological
                actives like exosomes cannot pass intact skin on their own. Devices solve this by opening thousands of
                transient micro-channels: ablative erbium (2,910&nbsp;nm / 2,940&nbsp;nm) and CO₂ (10,600&nbsp;nm) lasers vaporize
                tissue to form vertical channels, non-ablative lasers coagulate without removing tissue, and
                micro-needling (with or without radio-frequency) creates mechanical tracks while triggering the skin's
                own collagen-remodelling cascade.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  ["< 500 Da", "limit for passive skin diffusion"],
                  ["~30 min", "channels fully open after treatment"],
                  ["75% open", "channels still patent at 6 hours"],
                  ["20–40 µm", "coagulation zone acting as reservoir"],
                ].map(([v, l]) => (
                  <div key={l} className="border border-[#e4e9ec] p-3 text-center">
                    <p className="font-display text-xl">{v}</p>
                    <p className="font-lab mt-1 text-[9px] uppercase leading-relaxed tracking-[0.1em] text-[#56636b]">{l}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[13px] leading-relaxed text-[#56636b]">
                Published variables that decide how well an active reaches the dermis: channel density (benefits plateau
                around 100 micro-zones per cm²), depth (a 100–150 µm starting range at 5–10% density is common), the
                coagulation ring that can hold actives like a sponge, formulation viscosity (liquids and gels fill
                channels better than creams), and timing — the strongest uptake is measured when the biomaterial is
                applied immediately, within the first 30 minutes.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-6 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-biomaterials-card">
              <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">The cargo that rides the channels</p>
              <h3 className="font-display mt-3 text-2xl">Four biomaterial families in the literature</h3>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {[
                  ["PRP", "Platelet-rich plasma concentrates the blood's own growth factors (PDGF, TGF-β, VEGF, EGF) studied for fibroblast activation, angiogenesis and collagen synthesis. Preparation methods vary between clinics, so standardization remains an open research question."],
                  ["Exosomes", "30–150 nm vesicles carrying miRNA, mRNA, proteins and lipids. Studied for fibroblast signalling, collagen synthesis and immune modulation — with lower immunogenicity and easier storage and standardization than cell-based therapies."],
                  ["PDRN", "Polydeoxyribonucleotide, a DNA-derived fragment studied for adenosine-A2A-receptor activity, angiogenesis and anti-inflammatory effects in tissue-repair research."],
                  ["Scaffolds", "Natural or synthetic 3-D frameworks that mimic the extracellular matrix, supporting cell adhesion and migration — and acting as carriers that hold PRP, exosomes or PDRN in place."],
                ].map(([t, d]) => (
                  <div key={t} className="border border-[#e4e9ec] p-5">
                    <h4 className="font-display text-lg">{t}</h4>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#56636b]">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-6 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-synergy-card">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
                <div className="md:col-span-7">
                  <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">Clinic histology — laser ± exosomes</p>
                  <h3 className="font-display mt-3 text-2xl">What the combined therapy showed</h3>
                  <p className="mt-4 text-[13px] leading-relaxed text-[#56636b]">
                    In a 2,910-nm erbium-laser study, tissue treated with laser alone showed about a{" "}
                    <strong className="text-[#0a0a0a]">50% increase in type III collagen</strong> at six weeks — sites that
                    also received exosome application showed an approximately{" "}
                    <strong className="text-[#0a0a0a]">12-fold increase</strong>. Type IV collagen at the dermal-epidermal
                    junction rose ~3.5-fold, elastic fibres re-organized, and melanocyte dendricity in melasma cases
                    decreased further with exosomes. A second study — 2,910-nm laser with{" "}
                    <strong className="text-[#0a0a0a]">Exoplus Inc exosomes and VANTIS collagen scaffolds</strong> — showed
                    accelerated epidermal regeneration, neocollagenesis and newly organized vascular structures at two
                    weeks. The authors propose this reflects optimized channels (a 20–40 µm coagulation reservoir with
                    high-frequency delivery) recruiting stem cells into a supportive matrix environment.
                  </p>
                  <p className="font-lab mt-5 border-l-2 border-[#b4690e] bg-[#b4690e]/5 p-4 text-[10px] leading-relaxed text-[#56636b]">
                    Research-context findings involving professional energy-based devices and clinical procedures — not a
                    claim about Exoplus Bio cosmetic products, which are cosmetics intended to support the appearance of
                    skin and hair only.
                  </p>
                </div>
                <div className="md:col-span-5">
                  <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#56636b]">Type III collagen at 6 weeks — from the cited clinic study (illustrative rendering)</p>
                  <svg viewBox="0 0 320 240" className="mt-4 w-full" role="img" aria-label="Bar chart showing type III collagen: baseline, laser up 50 percent, laser plus exosome about twelve fold" data-testid="diagram-collagen3">
                    <line x1="20" y1="200" x2="310" y2="200" stroke="#e4e9ec" strokeWidth="1.5" />
                    {[
                      ["Baseline", 42, "#9aa7ad"],
                      ["Laser", 62, "#9fd9d5"],
                      ["Laser + exosome", 170, "#0e8c8c"],
                    ].map(([label, h, color], i) => (
                      <g key={label}>
                        <rect x={55 + i * 80} y={200 - h} width="42" height={h} fill={color} opacity="0.9" />
                        <text x={76 + i * 80} y="216" textAnchor="middle" fontSize="9" fill="#56636b">{label}</text>
                      </g>
                    ))}
                    <text x="156" y="20" textAnchor="middle" fontSize="10" fill="#56636b">relative expression (+50% vs ≈12×)</text>
                  </svg>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-6 border border-[#e4e9ec] bg-white p-6 md:p-8" data-testid="education-channels-download">
              <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
                <div>
                  <p className="font-lab text-[9px] uppercase tracking-[0.16em] text-[#0e8c8c]">Source document</p>
                  <h3 className="font-display mt-2 text-xl">Read the full research chapter (PDF)</h3>
                  <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-[#56636b]">
                    C. Cheng, <em>Regenerative Dermatology Through Energy-Based Channels: Transdermal Delivery and
                    Biological Synergy with Topical Biomaterials</em>, IntechOpen, © 2026 — distributed under the Creative
                    Commons Attribution (CC BY) license. Presented here for educational purposes.
                  </p>
                </div>
                <a
                  href="/research-energy-channels-cheng.pdf"
                  download="research-energy-channels-cheng.pdf"
                  data-testid="education-channels-pdf"
                  className="font-lab inline-flex shrink-0 items-center gap-2 bg-[#0a0a0a] px-7 py-3.5 text-[11px] uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#0e8c8c]"
                >
                  Download chapter PDF
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DOWNLOADABLE CARDS */}
      <section className="border-t border-[#e4e9ec] py-16 md:py-20" data-testid="education-cards-section">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Education resources · Free downloads</p>
            <h2 className="font-display mt-4 text-3xl tracking-tight md:text-4xl">Download the cards</h2>
            <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[#56636b]">
              Our patient-education card sets in six languages — take them with you, print them, or share them
              with your team. Hosted on Google Drive and downloaded directly.
            </p>
          </Reveal>
          {[
            {
              name: "What are Exosomes",
              format: "PDF",
              icon: FileText,
              testid: "what-exo",
              items: [
                { title: "What is Exosome? (EN)", id: "1N2oauyOuuZmaLyGPrNsiLuqtvXns7yBE" },
                { title: "What is Exosome? (CH)", id: "1ilxt-aKmRd6jOJGZ7CZCFNAqNJW81YUk" },
                { title: "What is Exosome? (FR)", id: "1qkX3P84_auTXrioYl0JcQYSIbw-zKDxe" },
                { title: "What is Exosome? (SP)", id: "1cgJ5xghyc8RplwNkmt9VapRCtpkypmNH" },
                { title: "What is Exosome? (TH)", id: "1ItAncDPsa25YTnNo5yz5sP2rWGCYoYhY" },
                { title: "What is Exosome? (VN)", id: "1tPSkGAu1XaM3XQ5Dn6Tsgh-LHkenwrSA" },
              ],
            },
            {
              name: "Benefits",
              format: "PNG",
              icon: ImageIcon,
              testid: "benefits",
              items: [
                { title: "Benefits (EN)", id: "1w1GZ7c2Wl4iUvy4FT_4gbBFxgb8FF4y0" },
                { title: "Benefits (CH)", id: "1gZGCRrtYQON4a7VwcqkjHAX0vNprpJO7" },
                { title: "Benefits (FR)", id: "1VpoCIDxSviFTgNfmMmEn6Yq_5zALRoHP" },
                { title: "Benefits (SP)", id: "1GLaTKmqJrHF-znWQbUDFgDeFBIDOSkmF" },
                { title: "Benefits (TH)", id: "1HpYbCiRy8xFq033l5j-LUl92wBIGJ6wo" },
                { title: "Benefits (VN)", id: "1-FkgvOmB07skcPqWYNjB1MinvvqPgUHX" },
              ],
            },
            {
              name: "Stem Cell vs Exosome",
              format: "PNG",
              icon: ImageIcon,
              testid: "stemcell",
              items: [
                { title: "Stem Cell vs Exosome (EN)", id: "15H2_Okqw52DFNA5UeAH0GChNhOURDmTq" },
                { title: "Stem Cell vs Exosome (CH)", id: "1Gk-3HlveOSzOVO5WXEPyNclA9F1upE6Q" },
                { title: "Stem Cell vs Exosome (FR)", id: "1xtTL722o3F5Mgs8AuHLq2gTgdEs3y9UE" },
                { title: "Stem Cell vs Exosome (SP)", id: "1Qv0FpGSSSY_z27U0wTPG9vqsgLwhkRJ5" },
                { title: "Stem Cell vs Exosome (TH)", id: "12eUXwpjtMJOmFkv__WyblBCtUwHuNSBl" },
                { title: "Stem Cell vs Exosome (VN)", id: "1n4686CHp-8e7HWwRRrUMmMpSoYhD5vQF" },
              ],
            },
          ].map((series) => (
            <div key={series.name} className="mt-12">
              <Reveal>
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h3 className="font-display text-2xl">{series.name}</h3>
                  <p className="font-lab text-[10px] uppercase tracking-[0.18em] text-[#8a959c]">
                    Series · {series.format} · 6 languages
                  </p>
                </div>
              </Reveal>
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {series.items.map((item) => (
                  <Reveal key={item.id}>
                    <div
                      className="flex h-full flex-col justify-between border border-[#e4e9ec] bg-white p-6"
                      data-testid={`education-card-${series.testid}-${item.title.slice(-4).replace(/[?() ]/g, "").toLowerCase()}`}
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          <series.icon className="h-6 w-6 text-[#0e8c8c]" />
                          <span className="font-lab border border-[#e4e9ec] px-2 py-1 text-[9px] uppercase tracking-[0.16em] text-[#56636b]">
                            {series.format}
                          </span>
                        </div>
                        <h4 className="mt-4 text-base font-medium leading-snug text-[#0a0a0a]">{item.title}</h4>
                        <p className="mt-2 text-xs leading-relaxed text-[#8a959c]">Direct download from Google Drive.</p>
                      </div>
                      <a
                        href={`https://drive.google.com/uc?export=download&id=${item.id}`}
                        download
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`education-dl-${series.testid}-${item.title.slice(-4).replace(/[?() ]/g, "").toLowerCase()}`}
                        className="mt-5 inline-flex items-center justify-center gap-2 bg-[#0a0a0a] px-5 py-3 text-sm text-white transition-colors hover:bg-[#0e8c8c]"
                      >
                        <Download className="h-4 w-4" /> Download {series.format}
                      </a>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DISCLAIMER + ATTRIBUTION */}
      <section className="border-t border-[#e4e9ec] bg-[#070b0c] py-20 text-white md:py-24" data-testid="education-disclaimer">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <p className="font-lab text-[10px] uppercase tracking-[0.22em] text-[#0e8c8c]">Disclaimer</p>
            <p className="mt-6 text-[15px] leading-relaxed text-white/75">
              The information on this page is provided for <strong className="text-white">educational purposes only</strong> and
              does not constitute medical advice. Content describing exosome mechanisms, research directions or
              handling reflects general scientific literature and industry practice — it is not a recommendation,
              promotion or prescription of any therapy, treatment or protocol. Exoplus Bio products are cosmetics,
              intended to support the appearance of skin and hair only; they are not approved drugs or natural health
              products and are not intended to diagnose, treat, cure or prevent any disease. Individual results vary.
              Treatment decisions should always be made between a patient and their physician.
            </p>
            <p className="mt-6 text-[15px] leading-relaxed text-white/75" data-testid="education-legal">
              {COMPLIANCE}
            </p>
            <p className="font-lab mt-8 border-t border-white/10 pt-6 text-[10px] uppercase tracking-[0.16em] text-white/40">
              Educational content adapted in part from BioExo Dynamics (bioexodynamics.com) and from C. Cheng,
              "Regenerative Dermatology Through Energy-Based Channels" (IntechOpen, CC BY 2026) · Educational use only
            </p>
            <Link
              to="/"
              data-testid="education-collection-link"
              className="font-lab mt-8 inline-flex items-center gap-2 bg-white px-7 py-3.5 text-[11px] uppercase tracking-[0.16em] text-[#070b0c] transition-colors hover:bg-[#0e8c8c] hover:text-white"
            >
              Explore the collection
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}